import type { UseMutationResult } from '@tanstack/react-query';
import { isStartingAtom, userInfoAtom } from 'app/atoms';
import { useAddAddressPromptCard } from 'app/components/add-address-prompt-card';
import { useInsuredUserOptional } from 'app/components/insured-users';
import type { TopLoadingIndicatorState } from 'app/components/top-loading-indicator';
import { useScopedTranslation } from 'app/hooks/i18n';
import { useDeviceWidth } from 'app/hooks/use-device-size';
import { useDistanceUnit } from 'app/hooks/use-distance-unit';
import { useSearch } from 'app/hooks/use-search';
import { Event, track } from 'app/lib/analytics';
import { getI18nLanguage } from 'app/lib/language';
import { sortCategories } from 'app/network/category';
import { getListingsFilters, useAllTags } from 'app/network/listing';
import { MAX_RADIUS_IN_METERS, useOnePreference } from 'app/network/preferences';
import { useTrialFreeSubAvailable } from 'app/network/promotions';
import type { CustomTabFilters } from 'app/schemas/user';
import { EmptyBadge } from 'app/screens/billboard/all-listings/components/listings-filters';
import {
  Strategies,
  useRecommendationRuntime,
  useMatchingRequestsRecommendationsWithRuntime,
} from 'app/screens/billboard/all-listings/components/recommended-listings';
import type { Address } from 'app/types/address';
import type { ServiceCategory, Category } from 'app/types/category';
import type { Group } from 'app/types/group';
import type { Listing, TransactionMode } from 'app/types/listing';
import { ListingSubtype, ListingType } from 'app/types/listing';
import type { FilterPreferences } from 'app/types/listings-filters';
import { type LastExplorationDate, type Preference } from 'app/types/preferences';
import { FeatureType } from 'app/types/recommender';
import { FeatureFlag, isFeatureEnabled, isWhiteLabel } from 'app/utils/helpers';
import { WhiteLabel } from 'app/types/white-labels';
import { useUISize } from 'app/utils/web-helpers';
import { useAtomValue } from 'jotai/index';
import type { Dispatch, SetStateAction } from 'react';
import { useEffect, useRef } from 'react';
import type { FlashOrFlatList } from 'app/design/primitives/page/list';
import type { TFunction } from 'i18next';

export enum TagType {
  Group = 'group',
  Address = 'address',
  Category = 'category',
  ListingTag = 'listingTag',
  ServiceCategory = 'serviceCategory',
  Subtype = 'subType',
  Distance = 'distance',
  HideFoundRequests = 'hideFoundRequests',
  HideOffersWithFee = 'hideOffersWithFee',
  DismissedOnly = 'dismissedOnly',
  TransactionMode = 'transactionMode',
}

interface UseRenderedListParams {
  appliedFilters: FilterPreferences;
  search: string;
  setSearch: (search: string) => void;
  forcedFilters: CustomTabFilters;
  isLoggedIn: boolean;
  isLoadingState?: TopLoadingIndicatorState;
  lastExplorationDateMutation?: UseMutationResult<
    Preference<'lastExplorationDate'> | null,
    Error,
    LastExplorationDate | null
  >;
  newAddressBadgeState?: [boolean, Dispatch<SetStateAction<boolean>>];
  hideFilterButton?: boolean;
}

function getAvailableSubtypes(appliedFilters: FilterPreferences, listingType: ListingType) {
  return appliedFilters.availableValues.subtypes.filter(
    (subtype) => subtype !== ListingSubtype.service || listingType === ListingType.request,
  );
}

function getSortState(appliedFilters: FilterPreferences, listingType: ListingType, listsServicesOnly: boolean) {
  if (listsServicesOnly) return appliedFilters.sortServicesState;
  if (listingType === ListingType.offer) return appliedFilters.sortOffersState;
  return appliedFilters.sortNeedsState;
}

function getSortedServiceCategories(
  selectedServiceCategories: string[] | undefined,
  serviceCategories: ServiceCategory[],
  t: TFunction,
) {
  return selectedServiceCategories
    ?.map((categoryId) => {
      const category = serviceCategories.find((candidate) => candidate.id === categoryId);
      if (!category) return undefined;
      return {
        id: category.id,
        name: t(`serviceCategories.${category.name}.name`, { ns: 'common' }),
      };
    })
    .filter((category): category is { id: string; name: string } => category !== undefined)
    .sort((first, second) => first.name.localeCompare(second.name));
}

interface BuildTagTextParams {
  allTags: { id: string; tagName: string }[] | undefined;
  i18nLanguage: string;
  id: string;
  possibleValues: (Address | Category | ServiceCategory | Group)[];
  t: TFunction;
  type: TagType;
  unit: string;
}

function getLocalizedCategoryName(category: Category | undefined, language: string) {
  return Object.entries(category?.name ?? {}).find(([key]) => key === language)?.[1] ?? '...';
}

function getTagText({ allTags, i18nLanguage, id, possibleValues, t, type, unit }: BuildTagTextParams): string {
  const value = possibleValues.find((candidate) => candidate.id === id);
  if (type === TagType.ListingTag) return allTags?.find((tag) => tag.id === id)?.tagName ?? '...';
  if (type === TagType.Distance) return t('distanceTag', { ns: 'listings-filters', distance: id, unit });
  if (type === TagType.Address) {
    return t('clubName', {
      ns: 'my-neighbourhood',
      clubName: value?.name ?? t('neighbourhoodClubName', { ns: 'my-clubs' }),
    });
  }
  if (type === TagType.Group) return `Club ${(value as Group | undefined)?.name ?? id}`;
  if (type === TagType.ServiceCategory) {
    return t(`serviceCategories.${(value as ServiceCategory | undefined)?.name ?? '???'}.name`, { ns: 'common' });
  }
  if (type === TagType.Subtype) return t(`subtype.${id}`, { ns: 'listings-filters' });
  if (type === TagType.HideFoundRequests) return t('hideFoundRequestsTag', { ns: 'listings-filters' });
  if (type === TagType.HideOffersWithFee) return t('hideOffersWithFeeTag', { ns: 'listings-filters' });
  if (type === TagType.DismissedOnly) return t('showDismissedOnlyTag', { ns: 'listings-filters' });
  if (type === TagType.TransactionMode) return t(`transactionMode.${id}`, { ns: 'listings-filters' });
  return getLocalizedCategoryName(value as Category | undefined, i18nLanguage);
}

function getFiltersAfterClosingTag(
  appliedFilters: FilterPreferences,
  id: string,
  type: TagType,
  searchDistance: number | undefined,
) {
  const {
    dismissedOnly,
    hideFoundRequests,
    hideOffersWithFee,
    selectedAddresses,
    selectedCategories,
    selectedGroups,
    selectedServiceCategories,
    selectedSubtypes,
    selectedTags,
    selectedTransactionModes,
  } = appliedFilters;

  return {
    searchDistance: type === TagType.Distance ? MAX_RADIUS_IN_METERS : searchDistance,
    selectedAddresses:
      type === TagType.Address ? selectedAddresses.filter((addressId) => addressId !== id) : selectedAddresses,
    selectedGroups: type === TagType.Group ? selectedGroups.filter((groupId) => groupId !== id) : selectedGroups,
    selectedCategories:
      type === TagType.Category ? selectedCategories?.filter((categoryId) => categoryId !== id) : selectedCategories,
    selectedServiceCategories:
      type === TagType.ServiceCategory
        ? selectedServiceCategories?.filter((categoryId) => categoryId !== id)
        : selectedServiceCategories,
    selectedSubtypes:
      type === TagType.Subtype
        ? selectedSubtypes?.filter((subtype) => subtype !== (id as ListingSubtype))
        : selectedSubtypes,
    hideFoundRequests: type === TagType.HideFoundRequests ? false : hideFoundRequests,
    hideOffersWithFee: type === TagType.HideOffersWithFee ? false : hideOffersWithFee,
    selectedTags: type === TagType.ListingTag ? selectedTags?.filter((tagId) => tagId !== id) : selectedTags,
    dismissedOnly: type === TagType.DismissedOnly ? false : dismissedOnly,
    selectedTransactionModes:
      type === TagType.TransactionMode
        ? (selectedTransactionModes ?? []).filter((mode) => mode !== (id as TransactionMode))
        : selectedTransactionModes,
  };
}

function getFilterTagVisibility({
  appliedFilters,
  availableSubtypes,
  listingType,
  searchDistance,
}: {
  appliedFilters: FilterPreferences;
  availableSubtypes: ListingSubtype[];
  listingType: ListingType;
  searchDistance: number | undefined;
}) {
  const {
    dismissedOnly,
    hideFoundRequests,
    hideOffersWithFee,
    selectedAddresses,
    selectedCategories,
    selectedGroups,
    selectedServiceCategories,
    selectedSubtypes,
    selectedTags,
    selectedTransactionModes,
  } = appliedFilters;
  const { addresses = [], groups = [] } = appliedFilters.availableValues;
  const visibility = {
    showGroupsFilterTags: selectedGroups.length > 0 && selectedGroups.length < groups.length,
    showAddressesFilterTags: selectedAddresses.length > 0 && selectedAddresses.length < addresses.length,
    showCategoriesFilterTags: Boolean(selectedCategories?.length),
    showServiceCategoriesFilterTags: Boolean(selectedServiceCategories?.length),
    showTagsFilterTags: Boolean(selectedTags?.length),
    showSubtypesFilterTags: Boolean(
      selectedSubtypes && selectedSubtypes.length > 0 && selectedSubtypes.length < availableSubtypes.length,
    ),
    showDistanceTag: Boolean(
      (selectedAddresses.length || selectedGroups.length) &&
      (searchDistance ?? MAX_RADIUS_IN_METERS) < MAX_RADIUS_IN_METERS,
    ),
    showHideFoundRequestsTag: listingType === ListingType.request && hideFoundRequests,
    showHideOffersWithFeeTag: listingType === ListingType.offer && hideOffersWithFee,
    showDismissedOnlyTag: listingType === ListingType.request && dismissedOnly,
    showTransactionModeTags:
      isWhiteLabel(WhiteLabel.HauteGaspesie) &&
      listingType === ListingType.offer &&
      (selectedTransactionModes?.length ?? 0) > 0 &&
      (selectedTransactionModes?.length ?? 0) < 3,
  };

  return { ...visibility, showFiltersTags: Object.values(visibility).some(Boolean) };
}

function getFilterCount(
  appliedFilters: FilterPreferences,
  visibility: ReturnType<typeof getFilterTagVisibility>,
  showNewAddressBadge: boolean | undefined,
) {
  if (showNewAddressBadge) return EmptyBadge;
  const {
    selectedAddresses,
    selectedCategories,
    selectedGroups,
    selectedServiceCategories,
    selectedSubtypes,
    selectedTags,
    selectedTransactionModes,
  } = appliedFilters;
  return (
    (visibility.showCategoriesFilterTags ? (selectedCategories?.length ?? 0) : 0) +
    (visibility.showServiceCategoriesFilterTags ? (selectedServiceCategories?.length ?? 0) : 0) +
    (visibility.showTagsFilterTags ? (selectedTags?.length ?? 0) : 0) +
    (visibility.showSubtypesFilterTags ? (selectedSubtypes?.length ?? 0) : 0) +
    (visibility.showGroupsFilterTags ? selectedGroups.length : 0) +
    (visibility.showAddressesFilterTags ? selectedAddresses.length : 0) +
    (visibility.showDistanceTag ? 1 : 0) +
    (visibility.showHideFoundRequestsTag ? 1 : 0) +
    (visibility.showHideOffersWithFeeTag ? 1 : 0) +
    (visibility.showDismissedOnlyTag ? 1 : 0) +
    (visibility.showTransactionModeTags ? (selectedTransactionModes?.length ?? 0) : 0)
  );
}

export function useRenderedList({
  appliedFilters,
  search,
  setSearch,
  forcedFilters,
  isLoggedIn,
  isLoadingState,
  lastExplorationDateMutation,
  newAddressBadgeState,
  hideFilterButton,
}: UseRenderedListParams) {
  const { t } = useScopedTranslation();
  const i18nLanguage = getI18nLanguage();
  const userInfo = useAtomValue(userInfoAtom);
  const canPublish = isFeatureEnabled(FeatureFlag.publishListings, userInfo?.featureFlags);

  const listingType = forcedFilters.type;

  const isRequestType = listingType === ListingType.request;

  const {
    selectedAddresses,
    selectedGroups,
    selectedCategories,
    selectedTags,
    selectedServiceCategories,
    selectedSubtypes,
    selectedTransactionModes,
    hideFoundRequests,
    hideOffersWithFee,
    dismissedOnly,
    availableValues,
  } = appliedFilters;
  const [searchDistance, setSearchDistance] =
    listingType === ListingType.offer ? appliedFilters.searchDistanceState : [undefined, undefined];

  const { freeSubAvailable } = useTrialFreeSubAvailable()();

  const groups = availableValues.groups ?? [];
  const addresses = availableValues.addresses ?? [];
  const categories = availableValues.categories ?? [];
  const serviceCategories = availableValues.serviceCategories ?? [];
  const availableSubtypes = getAvailableSubtypes(appliedFilters, listingType);

  const listsServicesOnly =
    forcedFilters.subtypes?.length === 1 && forcedFilters.subtypes[0] === ListingSubtype.service;

  const sortState = getSortState(appliedFilters, listingType, listsServicesOnly);
  // Extract tuple members so they can be used as stable deps in hooks below.
  const [sortValue, setSortValue] = sortState;

  const selectedCategoryIds = new Set(selectedCategories);
  const sortedSelectedCategories = sortCategories(
    categories.filter((category) => selectedCategoryIds.has(category.id)),
    i18nLanguage,
  );

  const sortedSelectedServiceCategories = getSortedServiceCategories(selectedServiceCategories, serviceCategories, t);

  const w = useDeviceWidth();
  const lastExplorationDateQuery = useOnePreference('lastExplorationDate');

  const hasStartedBrowsing = useRef(false);

  useEffect(() => {
    if (isLoadingState?.isLoading) {
      hasStartedBrowsing.current = false;
    }
  }, [isLoadingState?.isLoading]);

  const scrollBeginHandler = async () => {
    if (!hasStartedBrowsing.current) {
      hasStartedBrowsing.current = true;
      if (isLoggedIn) {
        await lastExplorationDateQuery.refetch();
        lastExplorationDateMutation?.mutate({
          ...(lastExplorationDateQuery.data?.value ?? {}),
          [listingType]: new Date().toISOString(),
        });
      }

      track(Event.BrowseListings, {
        listingsType: listingType,
        category: selectedCategories?.join(','),
        serviceCategory: selectedServiceCategories?.join(','),
        subtypes: selectedSubtypes?.join(','),
        search,
        sortBy: sortValue,
        ...(selectedGroups.length < groups.length ? { groups: selectedGroups } : {}),
        ...(selectedAddresses.length < addresses.length ? { addresses: selectedAddresses } : {}),
      });
    }
  };

  const { unit } = useDistanceUnit();
  const { data: allTags } = useAllTags();

  const buildTagText = (
    id: string,
    possibleValues: (Address | Category | ServiceCategory | Group)[],
    type: TagType,
  ): string => getTagText({ allTags, i18nLanguage, id, possibleValues, t, type, unit });

  const closeTag = (id: string, type: TagType) => {
    appliedFilters.setFilters(getFiltersAfterClosingTag(appliedFilters, id, type, searchDistance));
  };

  const { doSearch } = useSearch({ search, setSearch, sortValue, setSortValue });
  const filters = getListingsFilters(
    appliedFilters.forQuery,
    search,
    listingType,
    isLoggedIn,
    forcedFilters,
    sortValue,
  );

  const tagVisibility = getFilterTagVisibility({ appliedFilters, availableSubtypes, listingType, searchDistance });
  const {
    showGroupsFilterTags,
    showAddressesFilterTags,
    showCategoriesFilterTags,
    showServiceCategoriesFilterTags,
    showTagsFilterTags,
    showSubtypesFilterTags,
    showDistanceTag,
    showHideFoundRequestsTag,
    showHideOffersWithFeeTag,
    showDismissedOnlyTag,
    showTransactionModeTags,
    showFiltersTags,
  } = tagVisibility;

  const [showNewAddressBadge, setShowNewAddressBadge] = newAddressBadgeState ?? [];
  const numFilters = getFilterCount(appliedFilters, tagVisibility, showNewAddressBadge);

  const showAddAddressPrompt = useAddAddressPromptCard();

  const recommendationRuntime = useRecommendationRuntime();
  const {
    show: showRequestsRecommendations,
    recommendations: requestsRecommendations,
    isPending: isRequestsRecommendationsPending,
  } = useMatchingRequestsRecommendationsWithRuntime(
    {
      strategy: Strategies.UseMatchNeedsWithOffers,
      recommendationType: ListingType.request,
      similarityType: FeatureType.CONTENT,
    },
    recommendationRuntime,
  );
  const showInsuredInfo = useInsuredUserOptional();

  const { isSmallUI, isLargeUI } = useUISize();

  const listRef = useRef<FlashOrFlatList<Listing>>(null);

  // If the user searched, but is using a distance filter, offer them to remove that filter to get more results
  const promptToLookFurther =
    listingType === ListingType.offer && search && searchDistance && searchDistance < MAX_RADIUS_IN_METERS;

  const isStarting = useAtomValue(isStartingAtom);

  const showFiltersButton = isLoggedIn && !hideFilterButton;

  const hideListingsSubtype = forcedFilters.subtypes?.length === 1;

  return {
    t,
    i18nLanguage,
    userInfo,
    canPublish,
    listingType,
    dismissedOnly,
    isRequestType,
    selectedAddresses,
    selectedGroups,
    selectedCategories,
    selectedTags,
    selectedServiceCategories,
    selectedSubtypes,
    hideFoundRequests,
    hideOffersWithFee,
    searchDistance,
    setSearchDistance,
    freeSubAvailable,
    groups,
    addresses,
    categories,
    serviceCategories,
    availableSubtypes,
    listsServicesOnly,
    sortState,
    sortValue,
    setSortValue,
    sortedSelectedCategories,
    sortedSelectedServiceCategories,
    w,
    scrollBeginHandler,
    buildTagText,
    closeTag,
    doSearch,
    filters,
    showGroupsFilterTags,
    showAddressesFilterTags,
    showCategoriesFilterTags,
    showServiceCategoriesFilterTags,
    showDismissedOnlyTag,
    showTagsFilterTags,
    showSubtypesFilterTags,
    showDistanceTag,
    showHideFoundRequestsTag,
    showHideOffersWithFeeTag,
    showTransactionModeTags,
    selectedTransactionModes,
    showFiltersTags,
    showNewAddressBadge,
    setShowNewAddressBadge,
    numFilters,
    showAddAddressPrompt,
    showRequestsRecommendations,
    requestsRecommendations,
    isRequestsRecommendationsPending,
    showInsuredInfo,
    isSmallUI,
    isLargeUI,
    listRef,
    promptToLookFurther,
    isStarting,
    showFiltersButton,
    hideListingsSubtype,
    appliedFilters,
    search,
  };
}

export type UseRenderedListReturn = ReturnType<typeof useRenderedList>;
